import { ChevronsUpDown, LogOut, BadgeCheck, CreditCard, Moon, Sun, Monitor } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/Sidebar';
import { useGetUser } from '@/hooks/useGetUser';
import { useTheme } from '@/components/ThemeProvider/ThemeProvider';

const HUB_URL = import.meta.env.VITE_HUB_URL ?? '';

function getUserInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.charAt(0) ?? '';
  const last = lastName?.charAt(0) ?? '';
  return (first + last).toUpperCase() || '?';
}

function getUserDisplayName(firstName?: string, lastName?: string, email?: string): string {
  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ');
  }
  return email || 'User';
}

export function NavUser() {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  const { currentUser, logOutUser } = useGetUser();
  const { theme, setTheme } = useTheme();

  const displayName = getUserDisplayName(
    currentUser?.firstName,
    currentUser?.lastName,
    currentUser?.email,
  );
  const initials = getUserInitials(currentUser?.firstName, currentUser?.lastName);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <Avatar className='h-8 w-8 rounded-lg'>
                <AvatarFallback className='rounded-lg'>{initials}</AvatarFallback>
              </Avatar>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-semibold'>{displayName}</span>
                {currentUser?.email && (
                  <span className='truncate text-xs text-muted-foreground'>
                    {currentUser.email}
                  </span>
                )}
              </div>
              <ChevronsUpDown className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}
          >
            <DropdownMenuLabel className='p-0 font-normal'>
              <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarFallback className='rounded-lg'>{initials}</AvatarFallback>
                </Avatar>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-semibold'>{displayName}</span>
                  {currentUser?.email && (
                    <span className='truncate text-xs text-muted-foreground'>
                      {currentUser.email}
                    </span>
                  )}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <a href={`${HUB_URL}profile`}>
                  <BadgeCheck className='mr-2 h-4 w-4' />
                  {t('NavUser.my_profile')}
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href={`${HUB_URL}billing`}>
                  <CreditCard className='mr-2 h-4 w-4' />
                  {t('NavUser.billing')}
                </a>
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  {theme === 'dark' ? (
                    <Moon className='mr-2 h-4 w-4' />
                  ) : theme === 'light' ? (
                    <Sun className='mr-2 h-4 w-4' />
                  ) : (
                    <Monitor className='mr-2 h-4 w-4' />
                  )}
                  {t('NavUser.theme')}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onClick={() => setTheme('light')}>
                      <Sun className='mr-2 h-4 w-4' />
                      {t('NavUser.theme_light')}
                      {theme === 'light' && <span className='ml-auto text-xs'>✓</span>}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme('dark')}>
                      <Moon className='mr-2 h-4 w-4' />
                      {t('NavUser.theme_dark')}
                      {theme === 'dark' && <span className='ml-auto text-xs'>✓</span>}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme('system')}>
                      <Monitor className='mr-2 h-4 w-4' />
                      {t('NavUser.theme_system')}
                      {theme === 'system' && <span className='ml-auto text-xs'>✓</span>}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => logOutUser()}>
              <LogOut className='mr-2 h-4 w-4' />
              {t('NavUser.sign_out')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
